const DB_NAME = 'ar-gestion-guard-operations';
const DB_VERSION = 4;
const EVENT_STORE = 'events';
const MEDIA_STORE = 'media';
const META_STORE = 'meta';
const REPORT_STORE = 'shareReports';
const ACTIVE_ACCOUNT_KEY = 'ar_guard_offline_account_v2';
const OWNER_SUFFIX = ':owner:';
const LEGACY_MIGRATION_KEY = 'local-owner-migration:v4';
const LEGACY_QUARANTINE_KEY = 'ar_guard_legacy_quarantine_v4';
let legacyMigrationFlight = null;

const activeAccount = () => {
  try { return JSON.parse(window.localStorage.getItem(ACTIVE_ACCOUNT_KEY) || 'null'); }
  catch { return null; }
};

const activeOwnerId = () => String(activeAccount()?.userId || '').trim() || null;
const scopedMetaKey = (key, ownerId = activeOwnerId()) => `${key}${OWNER_SUFFIX}${ownerId || 'unbound'}`;

const jwtSubject = (token) => {
  try {
    const encoded = String(token || '').split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(encoded)).sub || null;
  } catch { return null; }
};

const trustedLegacyOwnerHint = (userId) => {
  try {
    const cached = JSON.parse(window.localStorage.getItem('ar_authenticated_user_cache') || 'null');
    return Boolean(cached?.id && cached.id === userId && jwtSubject(window.localStorage.getItem('access_token')) === userId);
  } catch { return false; }
};

const openDatabase = () => new Promise((resolve, reject) => {
  if (!('indexedDB' in window)) return reject(new Error('El almacenamiento sin internet no está disponible'));
  const request = window.indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    const upgrade = request.transaction;
    if (!database.objectStoreNames.contains(EVENT_STORE)) {
      const events = database.createObjectStore(EVENT_STORE, { keyPath: 'clientEventId' });
      events.createIndex('scopeStatusSequence', ['condominiumId', 'status', 'sequenceNumber']);
      events.createIndex('scopeSequence', ['condominiumId', 'sequenceNumber'], { unique: true });
      events.createIndex('ownerScopeStatusSequence', ['ownerUserId', 'condominiumId', 'status', 'sequenceNumber']);
    } else {
      const events = upgrade.objectStore(EVENT_STORE);
      if (!events.indexNames.contains('ownerScopeStatusSequence')) events.createIndex('ownerScopeStatusSequence', ['ownerUserId', 'condominiumId', 'status', 'sequenceNumber']);
    }
    if (!database.objectStoreNames.contains(MEDIA_STORE)) {
      const media = database.createObjectStore(MEDIA_STORE, { keyPath: 'id' });
      media.createIndex('eventId', 'clientEventId');
      media.createIndex('ownerScopeStatus', ['ownerUserId', 'condominiumId', 'status']);
    } else {
      const media = upgrade.objectStore(MEDIA_STORE);
      if (!media.indexNames.contains('ownerScopeStatus')) media.createIndex('ownerScopeStatus', ['ownerUserId', 'condominiumId', 'status']);
    }
    if (!database.objectStoreNames.contains(META_STORE)) database.createObjectStore(META_STORE, { keyPath: 'key' });
    if (!database.objectStoreNames.contains(REPORT_STORE)) {
      const reports = database.createObjectStore(REPORT_STORE, { keyPath: 'key' });
      reports.createIndex('scopeStatus', ['condominiumId', 'status']);
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('No se pudo abrir el almacenamiento'));
});

const requestResult = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const transactionDone = (transaction) => new Promise((resolve, reject) => {
  transaction.oncomplete = resolve;
  transaction.onerror = () => reject(transaction.error);
  transaction.onabort = () => reject(transaction.error || new Error('No se pudo guardar'));
});

const legacyMetaRecord = (item) => /^(package:|draft-media:|active-shift:|attendance-draft:|operations:)/.test(String(item?.key || ''))
  && !String(item.key).includes(OWNER_SUFFIX);

async function migrateOrQuarantineLegacyGuardData(ownerId, trustedOwner) {
  const database = await openDatabase();
  try {
    const marker = await requestResult(database.transaction(META_STORE, 'readonly').objectStore(META_STORE).get(LEGACY_MIGRATION_KEY));
    if (marker) return marker;
    const [events, media, meta, reports] = await Promise.all([
      requestResult(database.transaction(EVENT_STORE, 'readonly').objectStore(EVENT_STORE).getAll()),
      requestResult(database.transaction(MEDIA_STORE, 'readonly').objectStore(MEDIA_STORE).getAll()),
      requestResult(database.transaction(META_STORE, 'readonly').objectStore(META_STORE).getAll()),
      requestResult(database.transaction(REPORT_STORE, 'readonly').objectStore(REPORT_STORE).getAll()),
    ]);
    const legacyEvents = events.filter((item) => !item.ownerUserId);
    const legacyMedia = media.filter((item) => !item.ownerUserId);
    const legacyMeta = meta.filter(legacyMetaRecord);
    const legacyReports = reports.filter((item) => !item.ownerUserId);
    const total = legacyEvents.length + legacyMedia.length + legacyMeta.length + legacyReports.length;
    const migratedAt = new Date().toISOString();
    if (!total || !trustedOwner) {
      const status = total ? 'quarantined' : 'empty';
      const record = { key: LEGACY_MIGRATION_KEY, status, ownerUserId: null, itemCount: total, migratedAt };
      const transaction = database.transaction(META_STORE, 'readwrite');
      const done = transactionDone(transaction);
      const metaStore = transaction.objectStore(META_STORE);
      const winner = await requestResult(metaStore.get(LEGACY_MIGRATION_KEY));
      if (winner) { await done; return winner; }
      metaStore.put(record);
      await done;
      if (total) window.localStorage.setItem(LEGACY_QUARANTINE_KEY, JSON.stringify({ itemCount: total, detectedAt: migratedAt }));
      return record;
    }
    const metaKeys = new Set(meta.map((item) => item.key));
    const reportKeys = new Set(reports.map((item) => item.key));
    const transaction = database.transaction([EVENT_STORE, MEDIA_STORE, META_STORE, REPORT_STORE], 'readwrite');
    const done = transactionDone(transaction);
    const metaStore = transaction.objectStore(META_STORE);
    const winner = await requestResult(metaStore.get(LEGACY_MIGRATION_KEY));
    if (winner) { await done; return winner; }
    const eventStore = transaction.objectStore(EVENT_STORE);
    legacyEvents.forEach((item) => eventStore.put({ ...item, ownerUserId: ownerId, legacyEncryption: true, legacyActor: true }));
    const mediaStore = transaction.objectStore(MEDIA_STORE);
    legacyMedia.forEach((item) => mediaStore.put({ ...item, ownerUserId: ownerId, legacyEncryption: true }));
    legacyMeta.forEach((item) => {
      const targetKey = scopedMetaKey(item.key, ownerId);
      if (!metaKeys.has(targetKey)) metaStore.put({ ...item, key: targetKey, ownerUserId: ownerId, legacyEncryption: Boolean(item.valueCipher || item.items) });
      metaStore.delete(item.key);
    });
    const reportStore = transaction.objectStore(REPORT_STORE);
    legacyReports.forEach((item) => {
      const targetKey = `${item.key}${OWNER_SUFFIX}${ownerId}`;
      if (!reportKeys.has(targetKey)) reportStore.put({ ...item, key: targetKey, logicalKey: item.key, ownerUserId: ownerId });
      reportStore.delete(item.key);
    });
    const record = { key: LEGACY_MIGRATION_KEY, status: 'claimed', ownerUserId: ownerId, itemCount: total, migratedAt };
    metaStore.put(record);
    await done;
    window.localStorage.removeItem(LEGACY_QUARANTINE_KEY);
    return record;
  } finally { database.close(); }
}

const waitForLegacyMigration = () => legacyMigrationFlight || Promise.resolve();

const encryptionKey = async (database, ownerId = activeOwnerId()) => {
  if (!window.crypto?.subtle) throw new Error('Este dispositivo no ofrece almacenamiento seguro');
  const keyName = ownerId ? `operation-encryption-key:v2:${ownerId}` : 'operation-encryption-key:v1';
  const existing = await requestResult(database.transaction(META_STORE, 'readonly').objectStore(META_STORE).get(keyName));
  if (existing?.value) return existing.value;
  const value = await window.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  const transaction = database.transaction(META_STORE, 'readwrite');
  transaction.objectStore(META_STORE).add({ key: keyName, value, createdAt: new Date().toISOString() });
  try {
    await transactionDone(transaction);
    return value;
  } catch {
    const winner = await requestResult(database.transaction(META_STORE, 'readonly').objectStore(META_STORE).get(keyName));
    if (winner?.value) return winner.value;
    throw new Error('No fue posible preparar el almacenamiento seguro');
  }
};

const encryptBytes = async (key, bytes) => {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const cipher = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, bytes);
  return { cipher, iv: Array.from(iv) };
};

const decryptBytes = (key, cipher, iv) => window.crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(iv) }, key, cipher);

const hydratedEvent = async (database, event) => {
  if (!event?.payloadCipher) return event;
  const key = await encryptionKey(database, event.legacyEncryption ? null : event.ownerUserId || null);
  const plaintext = await decryptBytes(key, event.payloadCipher, event.payloadIv);
  return { ...event, payload: JSON.parse(new TextDecoder().decode(plaintext)) };
};

export const activateGuardOfflineAccount = (userId) => {
  const normalized = String(userId || '').trim();
  if (!normalized) return;
  const current = activeAccount();
  window.localStorage.setItem(ACTIVE_ACCOUNT_KEY, JSON.stringify({
    userId: normalized,
    actorSessionId: current?.userId === normalized ? current.actorSessionId || null : null,
  }));
  if (!legacyMigrationFlight) {
    legacyMigrationFlight = migrateOrQuarantineLegacyGuardData(normalized, trustedLegacyOwnerHint(normalized)).catch(() => null);
  }
  return legacyMigrationFlight;
};

export const getGuardLegacyQuarantine = () => {
  try { return JSON.parse(window.localStorage.getItem(LEGACY_QUARANTINE_KEY) || 'null'); }
  catch { return null; }
};

export async function bindGuardOfflineIdentity({ userId, actorSessionId, actorCredential, expiresAt }) {
  await waitForLegacyMigration();
  const normalized = String(userId || '').trim();
  if (!normalized || !actorSessionId || !actorCredential) throw new Error('No fue posible identificar el turno en este dispositivo');
  const database = await openDatabase();
  try {
    const key = await encryptionKey(database, normalized);
    const protectedCredential = await encryptBytes(key, new TextEncoder().encode(actorCredential));
    const transaction = database.transaction(META_STORE, 'readwrite');
    transaction.objectStore(META_STORE).put({
      key: `actor:${normalized}:${actorSessionId}`,
      ownerUserId: normalized,
      credentialCipher: protectedCredential.cipher,
      credentialIv: protectedCredential.iv,
      expiresAt: expiresAt || null,
      savedAt: new Date().toISOString(),
    });
    await transactionDone(transaction);
    window.localStorage.setItem(ACTIVE_ACCOUNT_KEY, JSON.stringify({ userId: normalized, actorSessionId }));
  } finally { database.close(); }
}

export async function getActiveGuardIdentity() {
  await waitForLegacyMigration();
  const account = activeAccount();
  if (!account?.userId || !account?.actorSessionId) return null;
  const database = await openDatabase();
  try {
    const record = await requestResult(database.transaction(META_STORE, 'readonly').objectStore(META_STORE)
      .get(`actor:${account.userId}:${account.actorSessionId}`));
    if (!record?.credentialCipher) return null;
    if (record.expiresAt && new Date(record.expiresAt).getTime() <= Date.now()) return null;
    const key = await encryptionKey(database, account.userId);
    const plaintext = await decryptBytes(key, record.credentialCipher, record.credentialIv);
    return { userId: account.userId, actorSessionId: account.actorSessionId,
      actorCredential: new TextDecoder().decode(plaintext), expiresAt: record.expiresAt || null };
  } finally { database.close(); }
}

export const getDeviceKey = () => {
  const storageKey = 'ar_guard_device_key';
  let value = window.localStorage.getItem(storageKey);
  if (!value) {
    value = window.crypto?.randomUUID?.() || `device-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem(storageKey, value);
  }
  return value;
};

const nextSequence = async (database, condominiumId) => {
  const transaction = database.transaction(META_STORE, 'readwrite');
  const store = transaction.objectStore(META_STORE);
  const key = `sequence:${condominiumId}`;
  const current = await requestResult(store.get(key));
  const value = Number(current?.value || 0) + 1;
  store.put({ key, value });
  await transactionDone(transaction);
  return value;
};

export async function queueGuardOperation({ condominiumId, eventType, payload = {}, entityKey = null, baseVersion = null, media = [], allowWithoutActor = false }) {
  await waitForLegacyMigration();
  if (!condominiumId) throw new Error('Selecciona el condominio antes de continuar');
  let identity = await getActiveGuardIdentity();
  if (!identity && allowWithoutActor && activeOwnerId()) {
    // Emergency fallback: the syncing authenticated account and registered
    // device are recorded server-side even if no guard shift identity exists.
    identity = { userId: activeOwnerId(), actorSessionId: null, actorCredential: null, syncingAccountFallback: true };
  }
  if (!identity) throw new Error('Espera a que la caseta termine de preparar tu sesión antes de registrar movimientos');
  const database = await openDatabase();
  try {
    const sequenceNumber = await nextSequence(database, condominiumId);
    const clientEventId = window.crypto?.randomUUID?.() || `event-${Date.now()}-${sequenceNumber}`;
    const key = await encryptionKey(database, identity.userId);
    const protectedPayload = await encryptBytes(key, new TextEncoder().encode(JSON.stringify(payload)));
    const event = {
      clientEventId, condominiumId, eventType, payloadCipher: protectedPayload.cipher, payloadIv: protectedPayload.iv, entityKey, baseVersion,
      sequenceNumber, deviceOccurredAt: new Date().toISOString(), status: 'pendiente',
      ownerUserId: identity.userId, actorSessionId: identity.actorSessionId, actorCredential: identity.actorCredential,
      legacyActor: Boolean(identity.syncingAccountFallback),
      attempts: 0, lastAttemptAt: null, serverEventId: null, conflictId: null,
    };
    const protectedMedia = [];
    for (let index = 0; index < media.length; index += 1) {
      const item = media[index];
      const encrypted = await encryptBytes(key, await item.blob.arrayBuffer());
      protectedMedia.push({ item, index, encrypted });
    }
    const transaction = database.transaction([EVENT_STORE, MEDIA_STORE], 'readwrite');
    transaction.objectStore(EVENT_STORE).add(event);
    protectedMedia.forEach(({ item, index, encrypted }) => transaction.objectStore(MEDIA_STORE).add({
      id: `${clientEventId}:${index}`, clientEventId, condominiumId,
      ownerUserId: identity.userId,
      encryptedBlob: new Blob([encrypted.cipher]), encryptionIv: encrypted.iv, name: item.name || `evidencia-${index + 1}`,
      type: item.type || item.blob?.type || 'application/octet-stream', status: 'pendiente',
    }));
    await transactionDone(transaction);
    navigator.serviceWorker?.ready.then((registration) => registration.sync?.register('ar-guard-operations')).catch(() => {});
    return event;
  } finally {
    database.close();
  }
}

export async function listPendingGuardOperations(condominiumId, limit = 25) {
  await waitForLegacyMigration();
  const database = await openDatabase();
  try {
    const transaction = database.transaction(EVENT_STORE, 'readonly');
    const all = await requestResult(transaction.objectStore(EVENT_STORE).getAll());
    const ownerId = activeOwnerId();
    const selected = all.filter((item) => item.condominiumId === condominiumId
      && ['pendiente', 'enviando'].includes(item.status)
      && (!item.legacyActor || item.ownerUserId === ownerId))
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber).slice(0, Math.min(limit, 25));
    return Promise.all(selected.map((item) => hydratedEvent(database, item)));
  } finally { database.close(); }
}

export async function countGuardOperations(condominiumId) {
  await waitForLegacyMigration();
  const database = await openDatabase();
  try {
    const all = await requestResult(database.transaction(EVENT_STORE, 'readonly').objectStore(EVENT_STORE).getAll());
    const ownerId = activeOwnerId();
    return all.filter((item) => item.condominiumId === condominiumId && (!ownerId || item.ownerUserId === ownerId)).reduce((summary, item) => {
      summary[item.status] = (summary[item.status] || 0) + 1;
      return summary;
    }, { pendiente: 0, enviado: 0, necesita_revision: 0 });
  } finally { database.close(); }
}

export async function listGuardOperations(condominiumId) {
  await waitForLegacyMigration();
  const database = await openDatabase();
  try {
    const all = await requestResult(database.transaction(EVENT_STORE, 'readonly').objectStore(EVENT_STORE).getAll());
    const ownerId = activeOwnerId();
    const selected = all.filter((item) => item.condominiumId === condominiumId && (!ownerId || item.ownerUserId === ownerId))
      .sort((a, b) => b.sequenceNumber - a.sequenceNumber);
    return Promise.all(selected.map((item) => hydratedEvent(database, item)));
  } finally { database.close(); }
}

export async function discardGuardOperation(clientEventId) {
  await waitForLegacyMigration();
  if (!clientEventId) return;
  const database = await openDatabase();
  try {
    const mediaRows = await requestResult(database.transaction(MEDIA_STORE, 'readonly').objectStore(MEDIA_STORE).getAll());
    const transaction = database.transaction([EVENT_STORE, MEDIA_STORE], 'readwrite');
    transaction.objectStore(EVENT_STORE).delete(clientEventId);
    const mediaStore = transaction.objectStore(MEDIA_STORE);
    mediaRows.filter((item) => item.clientEventId === clientEventId).forEach((item) => mediaStore.delete(item.id));
    await transactionDone(transaction);
  } finally { database.close(); }
}

export async function applyGuardAcknowledgements(acknowledgements) {
  await waitForLegacyMigration();
  if (!acknowledgements?.length) return;
  const database = await openDatabase();
  try {
    const transaction = database.transaction(EVENT_STORE, 'readwrite');
    const store = transaction.objectStore(EVENT_STORE);
    for (const acknowledgement of acknowledgements) {
      const event = await requestResult(store.get(acknowledgement.client_event_id));
      if (!event) continue;
      store.put({
        ...event,
        status: acknowledgement.status,
        serverEventId: acknowledgement.server_event_id || event.serverEventId,
        serverEntityId: acknowledgement.server_entity_id || event.serverEntityId || null,
        serverFolio: acknowledgement.folio || event.serverFolio || null,
        alertStatus: acknowledgement.alert_status || event.alertStatus || null,
        alertRecipientCount: acknowledgement.alert_recipient_count ?? event.alertRecipientCount ?? null,
        proofMethod: acknowledgement.proof_method || event.proofMethod || null,
        proofStatus: acknowledgement.proof_status || event.proofStatus || null,
        acknowledgementMessage: acknowledgement.message || null,
        duplicate: Boolean(acknowledgement.duplicate),
        conflictId: acknowledgement.conflict_id || null,
        lastAttemptAt: new Date().toISOString(),
        acknowledgedAt: acknowledgement.status === 'enviado' ? new Date().toISOString() : event.acknowledgedAt || null,
      });
    }
    await transactionDone(transaction);
  } finally { database.close(); }
}

export async function listPendingGuardMedia(condominiumId) {
  await waitForLegacyMigration();
  const database = await openDatabase();
  try {
    const media = await requestResult(database.transaction(MEDIA_STORE, 'readonly').objectStore(MEDIA_STORE).getAll());
    const events = await requestResult(database.transaction(EVENT_STORE, 'readonly').objectStore(EVENT_STORE).getAll());
    const acknowledged = new Map(events.map((item) => [item.clientEventId, item]));
    const ownerId = activeOwnerId();
    const selected = media.filter((item) => {
      const event = acknowledged.get(item.clientEventId);
      return item.condominiumId === condominiumId && item.status === 'pendiente'
        && event?.status === 'enviado' && (!event.legacyActor || event.ownerUserId === ownerId);
    });
    if (!selected.some((item) => item.encryptedBlob)) return selected;
    return Promise.all(selected.map(async (item) => {
      if (!item.encryptedBlob) return item;
      const key = await encryptionKey(database, item.legacyEncryption ? null : item.ownerUserId || null);
      const plaintext = await decryptBytes(key, await item.encryptedBlob.arrayBuffer(), item.encryptionIv);
      return { ...item, blob: new Blob([plaintext], { type: item.type }) };
    }));
  } finally { database.close(); }
}

export async function markGuardMediaAcknowledged(mediaId, serverMediaId) {
  await waitForLegacyMigration();
  const database = await openDatabase();
  try {
    const transaction = database.transaction(MEDIA_STORE, 'readwrite');
    const store = transaction.objectStore(MEDIA_STORE);
    const item = await requestResult(store.get(mediaId));
    if (item) store.put({ ...item, status: 'enviado', serverMediaId, acknowledgedAt: new Date().toISOString() });
    await transactionDone(transaction);
  } finally { database.close(); }
}

export async function saveGuardOfflinePackage(condominiumId, value, scope = 'operations') {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return;
  const database = await openDatabase();
  try {
    const key = await encryptionKey(database, ownerId);
    const protectedValue = await encryptBytes(key, new TextEncoder().encode(JSON.stringify(value)));
    const transaction = database.transaction(META_STORE, 'readwrite');
    transaction.objectStore(META_STORE).put({ key: scopedMetaKey(`package:${scope}:${condominiumId}`, ownerId), ownerUserId: ownerId, valueCipher: protectedValue.cipher, valueIv: protectedValue.iv, savedAt: new Date().toISOString() });
    await transactionDone(transaction);
  } finally { database.close(); }
}

export async function getGuardOfflinePackage(condominiumId, scope = 'operations') {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return null;
  const database = await openDatabase();
  try {
    const record = await requestResult(database.transaction(META_STORE, 'readonly').objectStore(META_STORE).get(scopedMetaKey(`package:${scope}:${condominiumId}`, ownerId)));
    if (!record?.valueCipher) return record?.value || null;
    const key = await encryptionKey(database, record.legacyEncryption ? null : ownerId);
    return JSON.parse(new TextDecoder().decode(await decryptBytes(key, record.valueCipher, record.valueIv)));
  } finally { database.close(); }
}

export async function saveGuardDraftMedia(condominiumId, items = [], scope = 'novelty') {
  await waitForLegacyMigration();
  if (!condominiumId) return;
  const ownerId = activeOwnerId();
  if (!ownerId) return;
  const database = await openDatabase();
  try {
    const recordKey = scopedMetaKey(`draft-media:${scope}:${condominiumId}`, ownerId);
    if (!items.length) {
      const transaction = database.transaction(META_STORE, 'readwrite');
      transaction.objectStore(META_STORE).delete(recordKey);
      await transactionDone(transaction);
      return;
    }
    const key = await encryptionKey(database, ownerId);
    const protectedItems = [];
    for (const [index, item] of items.entries()) {
      const blob = item?.blob || item?.file;
      if (!blob?.arrayBuffer) continue;
      const metadata = {
        name: String(item?.name || item?.file?.name || `evidencia-${index + 1}`).slice(0, 220),
        type: String(item?.type || blob.type || 'application/octet-stream').slice(0, 120),
        kind: String(item?.kind || (String(blob.type || '').startsWith('audio/') ? 'audio' : 'evidence')).slice(0, 40),
        size: Number(blob.size || 0),
        lastModified: Number(item?.lastModified || item?.file?.lastModified || Date.now()),
        quality: item?.quality && typeof item.quality === 'object' ? item.quality : null,
      };
      const [protectedBlob, protectedMetadata] = await Promise.all([
        encryptBytes(key, await blob.arrayBuffer()),
        encryptBytes(key, new TextEncoder().encode(JSON.stringify(metadata))),
      ]);
      protectedItems.push({
        cipher: new Blob([protectedBlob.cipher]), iv: protectedBlob.iv,
        metadataCipher: new Blob([protectedMetadata.cipher]), metadataIv: protectedMetadata.iv,
      });
    }
    const transaction = database.transaction(META_STORE, 'readwrite');
    transaction.objectStore(META_STORE).put({ key: recordKey, ownerUserId: ownerId, items: protectedItems, savedAt: new Date().toISOString() });
    await transactionDone(transaction);
  } finally { database.close(); }
}

export async function getGuardDraftMedia(condominiumId, scope = 'novelty') {
  await waitForLegacyMigration();
  if (!condominiumId) return [];
  const ownerId = activeOwnerId();
  if (!ownerId) return [];
  const database = await openDatabase();
  try {
    const record = await requestResult(database.transaction(META_STORE, 'readonly').objectStore(META_STORE)
      .get(scopedMetaKey(`draft-media:${scope}:${condominiumId}`, ownerId)));
    if (!Array.isArray(record?.items) || !record.items.length) return [];
    const key = await encryptionKey(database, record.legacyEncryption ? null : ownerId);
    return Promise.all(record.items.map(async (item) => {
      const [bytes, metadataBytes] = await Promise.all([
        decryptBytes(key, await item.cipher.arrayBuffer(), item.iv),
        decryptBytes(key, await item.metadataCipher.arrayBuffer(), item.metadataIv),
      ]);
      const metadata = JSON.parse(new TextDecoder().decode(metadataBytes));
      return {
        ...metadata,
        blob: new Blob([bytes], { type: metadata.type || 'application/octet-stream' }),
      };
    }));
  } finally { database.close(); }
}

export const clearGuardDraftMedia = (condominiumId, scope = 'novelty') => saveGuardDraftMedia(condominiumId, [], scope);

export async function saveActiveGuardShift(condominiumId, value) {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return;
  const database = await openDatabase();
  try {
    const transaction = database.transaction(META_STORE, 'readwrite');
    transaction.objectStore(META_STORE).put({ key: scopedMetaKey(`active-shift:${condominiumId}`, ownerId), ownerUserId: ownerId, value });
    await transactionDone(transaction);
    window.dispatchEvent(new CustomEvent('guard-shift-changed', { detail: { condominiumId, value } }));
  } finally { database.close(); }
}

export async function updateActiveGuardShiftIfCurrent(condominiumId, expectedSessionId, update, isCurrent = () => true, source = null) {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return { updated: false };
  const database = await openDatabase();
  try {
    if (!isCurrent()) return { updated: false };
    const transaction = database.transaction(META_STORE, 'readwrite');
    const done = transactionDone(transaction);
    done.catch(() => {}); // Also consume a transaction error if its read rejects first.
    const store = transaction.objectStore(META_STORE);
    const key = scopedMetaKey(`active-shift:${condominiumId}`, ownerId);
    const record = await requestResult(store.get(key));
    if (!isCurrent() || (record?.value?.sessionClientEventId || null) !== (expectedSessionId || null)) {
      await done;
      return { updated: false };
    }
    // The comparison and update share a read/write transaction. A newer turn
    // written by another screen cannot be overwritten from an old snapshot.
    const value = update(record?.value || null);
    store.put({ key, value });
    await done;
    if (isCurrent()) window.dispatchEvent(new CustomEvent('guard-shift-changed', { detail: { condominiumId, value, source } }));
    return { updated: true, value };
  } finally { database.close(); }
}

export async function getActiveGuardShift(condominiumId) {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return null;
  const database = await openDatabase();
  try {
    return (await requestResult(database.transaction(META_STORE, 'readonly').objectStore(META_STORE).get(scopedMetaKey(`active-shift:${condominiumId}`, ownerId))))?.value || null;
  } finally { database.close(); }
}

export async function saveGuardAttendanceDraft(condominiumId, value) {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return;
  const database = await openDatabase();
  try {
    const transaction = database.transaction(META_STORE, 'readwrite');
    transaction.objectStore(META_STORE).put({
      key: scopedMetaKey(`attendance-draft:${condominiumId}`, ownerId), ownerUserId: ownerId,
      value,
      savedAt: new Date().toISOString(),
    });
    await transactionDone(transaction);
  } finally { database.close(); }
}

export async function getGuardAttendanceDraft(condominiumId) {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return null;
  const database = await openDatabase();
  try {
    return (await requestResult(database.transaction(META_STORE, 'readonly').objectStore(META_STORE)
      .get(scopedMetaKey(`attendance-draft:${condominiumId}`, ownerId))))?.value || null;
  } finally { database.close(); }
}

export async function clearGuardAttendanceDraft(condominiumId) {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return;
  const database = await openDatabase();
  try {
    const transaction = database.transaction(META_STORE, 'readwrite');
    transaction.objectStore(META_STORE).delete(scopedMetaKey(`attendance-draft:${condominiumId}`, ownerId));
    await transactionDone(transaction);
  } finally { database.close(); }
}

export async function saveGuardOperationalState(condominiumId, value) {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return;
  const database = await openDatabase();
  try {
    const key = await encryptionKey(database, ownerId);
    const protectedValue = await encryptBytes(key, new TextEncoder().encode(JSON.stringify(value)));
    const transaction = database.transaction(META_STORE, 'readwrite');
    transaction.objectStore(META_STORE).put({ key: scopedMetaKey(`operations:${condominiumId}`, ownerId), ownerUserId: ownerId, valueCipher: protectedValue.cipher, valueIv: protectedValue.iv, savedAt: new Date().toISOString() });
    await transactionDone(transaction);
  } finally { database.close(); }
}

export async function getGuardOperationalState(condominiumId) {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return null;
  const database = await openDatabase();
  try {
    const record = await requestResult(database.transaction(META_STORE, 'readonly').objectStore(META_STORE).get(scopedMetaKey(`operations:${condominiumId}`, ownerId)));
    if (!record?.valueCipher) return record?.value || null;
    const key = await encryptionKey(database, record.legacyEncryption ? null : ownerId);
    return JSON.parse(new TextDecoder().decode(await decryptBytes(key, record.valueCipher, record.valueIv)));
  }
  finally { database.close(); }
}
export const saveGuardPatrolState = (condominiumId, value) => saveGuardOperationalState(`${condominiumId}:patrol`, value);
export const getGuardPatrolState = (condominiumId) => getGuardOperationalState(`${condominiumId}:patrol`);
export const saveGuardAccessState = (condominiumId, value) => saveGuardOperationalState(`${condominiumId}:access`, value);
export const getGuardAccessState = (condominiumId) => getGuardOperationalState(`${condominiumId}:access`);

export async function upsertGuardShareReport(report) {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return report;
  const database = await openDatabase();
  try { const transaction=database.transaction(REPORT_STORE,'readwrite'); transaction.objectStore(REPORT_STORE).put({ ...report, key: `${report.key}${OWNER_SUFFIX}${ownerId}`, logicalKey: report.key, ownerUserId: ownerId }); await transactionDone(transaction); return report; }
  finally { database.close(); }
}
export async function listGuardShareReports(condominiumId) {
  await waitForLegacyMigration();
  const ownerId = activeOwnerId();
  if (!ownerId) return [];
  const database=await openDatabase(); try { const rows=await requestResult(database.transaction(REPORT_STORE,'readonly').objectStore(REPORT_STORE).getAll()); return rows.filter((item)=>item.condominiumId===condominiumId && item.ownerUserId===ownerId).map((item)=>({ ...item, key: item.logicalKey || item.key })).sort((a,b)=>b.preparedAt.localeCompare(a.preparedAt)); } finally { database.close(); }
}

export async function markGuardBatchAttempt(events) {
  await waitForLegacyMigration();
  const database = await openDatabase();
  try {
    const transaction = database.transaction(EVENT_STORE, 'readwrite');
    const store = transaction.objectStore(EVENT_STORE);
    for (const event of events) {
      const { payload, ...storedEvent } = event;
      store.put({ ...storedEvent, status: 'pendiente', attempts: Number(event.attempts || 0) + 1, lastAttemptAt: new Date().toISOString() });
    }
    await transactionDone(transaction);
  } finally { database.close(); }
}

export async function removeConfirmedGuardData(condominiumId, olderThan) {
  await waitForLegacyMigration();
  const database = await openDatabase();
  try {
    const events = await requestResult(database.transaction(EVENT_STORE, 'readonly').objectStore(EVENT_STORE).getAll());
    const mediaRows = await requestResult(database.transaction(MEDIA_STORE, 'readonly').objectStore(MEDIA_STORE).getAll());
    const mediaPending = new Set(mediaRows.filter((item) => item.status !== 'enviado').map((item) => item.clientEventId));
    const removable = events.filter((item) => item.condominiumId === condominiumId && item.status === 'enviado'
      && (item.acknowledgedAt || item.deviceOccurredAt) < olderThan && !mediaPending.has(item.clientEventId));
    const transaction = database.transaction([EVENT_STORE, MEDIA_STORE], 'readwrite');
    removable.forEach((item) => transaction.objectStore(EVENT_STORE).delete(item.clientEventId));
    const ids = new Set(removable.map((item) => item.clientEventId));
    mediaRows.filter((item) => ids.has(item.clientEventId) && item.status === 'enviado').forEach((item) => transaction.objectStore(MEDIA_STORE).delete(item.id));
    await transactionDone(transaction);
    return removable.length;
  } finally { database.close(); }
}

export async function pruneGuardOfflineData(retentionDays = 7) {
  await waitForLegacyMigration();
  const database = await openDatabase();
  try {
    const cutoff = new Date(Date.now() - Math.max(1, retentionDays) * 86400000).toISOString();
    const events = await requestResult(database.transaction(EVENT_STORE, 'readonly').objectStore(EVENT_STORE).getAll());
    const media = await requestResult(database.transaction(MEDIA_STORE, 'readonly').objectStore(MEDIA_STORE).getAll());
    const pendingMedia = new Set(media.filter((item) => item.status !== 'enviado').map((item) => item.clientEventId));
    const removable = events.filter((item) => item.status === 'enviado'
      && (item.acknowledgedAt || item.deviceOccurredAt) < cutoff && !pendingMedia.has(item.clientEventId));
    if (!removable.length) return 0;
    const ids = new Set(removable.map((item) => item.clientEventId));
    const transaction = database.transaction([EVENT_STORE, MEDIA_STORE], 'readwrite');
    removable.forEach((item) => transaction.objectStore(EVENT_STORE).delete(item.clientEventId));
    media.filter((item) => ids.has(item.clientEventId)).forEach((item) => transaction.objectStore(MEDIA_STORE).delete(item.id));
    await transactionDone(transaction);
    return removable.length;
  } finally { database.close(); }
}

export async function detachGuardOfflineAccount(userId) {
  await waitForLegacyMigration();
  const ownerId = String(userId || activeOwnerId() || '').trim();
  const current = activeAccount();
  if (!ownerId || current?.userId === ownerId) window.localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
  if (!ownerId) return { quarantined: 0, removed: 0 };
  const database = await openDatabase();
  try {
    const events = await requestResult(database.transaction(EVENT_STORE, 'readonly').objectStore(EVENT_STORE).getAll());
    const media = await requestResult(database.transaction(MEDIA_STORE, 'readonly').objectStore(MEDIA_STORE).getAll());
    const meta = await requestResult(database.transaction(META_STORE, 'readonly').objectStore(META_STORE).getAll());
    const reports = await requestResult(database.transaction(REPORT_STORE, 'readonly').objectStore(REPORT_STORE).getAll());
    const ownerEvents = events.filter((item) => item.ownerUserId === ownerId);
    const ownerMedia = media.filter((item) => item.ownerUserId === ownerId);
    const pendingMediaEvents = new Set(ownerMedia.filter((item) => item.status !== 'enviado').map((item) => item.clientEventId));
    const retained = ownerEvents.filter((item) => ['pendiente', 'enviando'].includes(item.status) || pendingMediaEvents.has(item.clientEventId));
    const retainedIds = new Set(retained.map((item) => item.clientEventId));
    const retainedSessions = new Set(retained.map((item) => item.actorSessionId).filter(Boolean));
    const transaction = database.transaction([EVENT_STORE, MEDIA_STORE, META_STORE, REPORT_STORE], 'readwrite');
    ownerEvents.filter((item) => !retainedIds.has(item.clientEventId)).forEach((item) => transaction.objectStore(EVENT_STORE).delete(item.clientEventId));
    ownerMedia.filter((item) => !retainedIds.has(item.clientEventId)).forEach((item) => transaction.objectStore(MEDIA_STORE).delete(item.id));
    meta.filter((item) => {
      const belongs = item.ownerUserId === ownerId || String(item.key || '').endsWith(`${OWNER_SUFFIX}${ownerId}`)
        || item.key === `operation-encryption-key:v2:${ownerId}` || String(item.key || '').startsWith(`actor:${ownerId}:`);
      if (!belongs) return false;
      if (String(item.key || '').startsWith(`actor:${ownerId}:`)) return !retainedSessions.has(String(item.key).split(':').slice(2).join(':'));
      if (item.key === `operation-encryption-key:v2:${ownerId}`) return retained.length === 0;
      return true;
    }).forEach((item) => transaction.objectStore(META_STORE).delete(item.key));
    reports.filter((item) => item.ownerUserId === ownerId).forEach((item) => transaction.objectStore(REPORT_STORE).delete(item.key));
    await transactionDone(transaction);
    return { quarantined: retained.length, removed: ownerEvents.length - retained.length };
  } finally { database.close(); }
}
